/**
 * @fileoverview Redux store configuration with persistence and encryption
 * Configures the main application store with Redux Toolkit and Redux Persist
 * Implements encrypted storage for sensitive data like authentication tokens
 * 
 * @version 1.0.0
 * @author Absolute Jobs Team
 * @security Uses encrypted persistence for sensitive authentication data
 */

import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { persistStore, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage"; // defaults to localStorage for web
import AuthReducer from "../features/auth/AuthSlice";
import encryptTransform from "./encryptTransform";
import NavigationReducer from "../features/NavigationSlice";


/**
 * Redux Persist configuration with encryption
 * Configures how the Redux state is persisted to localStorage
 * 
 * @security Uses encryptTransform to encrypt sensitive data before storage
 */
const persistConfig = {
  /** Root key for localStorage storage */
  key: "root",
  /** Storage engine - defaults to localStorage */
  storage,
  /** Transform pipeline - includes encryption for security */
  transforms: [encryptTransform],
};

/**
 * Combined reducer with persistence applied
 * Combines all feature reducers and applies encryption persistence
 * 
 * @security AuthReducer state is encrypted when persisted
 */
const persistedReducer = persistReducer(
  persistConfig,
  combineReducers({
    /** Authentication state - contains user tokens and session data */
    auth: AuthReducer,
    /** Navigation state - tracks current route and navigation history */
    navigation: NavigationReducer,
    // Add reducers for other parts of your state if needed
  })
);

/**
 * Main Redux store configuration
 * Configures store with persisted reducers and custom middleware
 * 
 * @security Middleware configured to handle encrypted persistence safely
 */
const store = configureStore({
    /** Root reducer with persistence and encryption */
    reducer: persistedReducer,
    /** Middleware configuration with persistence compatibility */
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          // Ignore Redux Persist actions that contain non-serializable values
          ignoredActions: ["persist/PERSIST"],
        },
      }),
  });
  

/**
 * Redux Persist store persistor
 * Handles persistence lifecycle (rehydration, flush, etc.)
 */
export const persistor = persistStore(store);

/**
 * Root state type for TypeScript support
 * Inferred from the store's getState return type
 * 
 * @example
 * ```typescript
 * const selectUser = (state: RootState) => state.auth.user;
 * ```
 */
export type RootState = ReturnType<typeof store.getState>;

/**
 * App dispatch type for TypeScript support
 * Provides type safety for dispatching actions
 * 
 * @example
 * ```typescript
 * const dispatch: AppDispatch = useDispatch();
 * dispatch(loginUser({ email, password }));
 * ```
 */
export type AppDispatch = typeof store.dispatch;

/**
 * The main Redux store instance
 * Contains all application state with encryption and persistence
 * 
 * @security State is automatically encrypted when persisted to localStorage
 */
export default store;